// frontend/src/lib/kmlCore.js

export function buildMetaOverride(meta, color1set, shikii1set) {
  const c = Array.isArray(color1set) ? color1set : [];
  const s = Array.isArray(shikii1set) ? shikii1set : [];

  const pickColor = (i, fallback) => {
    const v = c[i];
    return v != null && v !== "" ? v : fallback;
  };

  return {
    ...meta,

    kml1_color: pickColor(0, meta.kml1_color),
    kml2_color: pickColor(1, meta.kml2_color),
    kml3_color: pickColor(2, meta.kml3_color),
    kml4_color: pickColor(3, meta.kml4_color),
    kml5_color: pickColor(4, meta.kml5_color),

    hanrei1_color: pickColor(0, meta.hanrei1_color),
    hanrei2_color: pickColor(1, meta.hanrei2_color),
    hanrei3_color: pickColor(2, meta.hanrei3_color),
    hanrei4_color: pickColor(3, meta.hanrei4_color),
    hanrei5_color: pickColor(4, meta.hanrei5_color),

    shikii1: s[0] ?? meta.shikii1,
    shikii2: s[1] ?? meta.shikii2,
    shikii3: s[2] ?? meta.shikii3,
    shikii4: s[3] ?? meta.shikii4,
    shikii5: s[4] ?? meta.shikii5,
  };
}

export const normalizeNFD = (s) => String(s ?? "").normalize("NFD");
export const toNumber = (v) => Number(v);

export function stripTrailingNewlines(text) {
  return String(text ?? "").replace(/\r?\n$/, "").replace(/\r?\n$/, "");
}

export function parseCsvRows(text) {
  return stripTrailingNewlines(text)
    .split("\n")
    .map((line) => line.replace("\r", ""))
    .map((line) => line.split(","));
}

const yearsCache = new Map();

function buildYearsKey(meta) {
  if (!meta) return "";

  const ys = Object.keys(meta)
    .filter((k) => /^year\d+$/.test(k))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)))
    .map((k) => (meta[k] ? String(meta[k]) : ""))
    .join("|");

  return `${meta?.no ?? ""}__${ys}`;
}

function getYearKeysSorted(meta) {
  if (!meta) return [];
  return Object.keys(meta)
    .filter((k) => /^year\d+$/.test(k))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)));
}

export function getYearsSorted(meta) {
  const keys = getYearKeysSorted(meta);
  return keys
    .map((k) => meta[k])
    .filter((v) => v != null && v !== "");
}

export function makeYears(meta) {
  const key = buildYearsKey(meta);
  if (yearsCache.has(key)) return yearsCache.get(key);

  let yearstmp = [];
  let yearsbeginendtmp = [];

  if (String(meta?.no) === "07-01") {
    yearstmp = ["1月", "4月", "7月", "10月", "1月"];
    yearsbeginendtmp = [
      ["2000-01-01", "2000-04-01"],
      ["2000-04-01", "2000-07-01"],
      ["2000-07-01", "2000-10-01"],
      ["2000-10-01", "2000-12-31"],
    ];
  } else {
    yearstmp = getYearsSorted(meta);

    const firstyear = yearstmp[0];
    const bflastyear = yearstmp.slice(-2)[0];
    const lastyear = yearstmp.slice(-1)[0];

    let lastyearend;
    if (firstyear === lastyear) {
      lastyearend = toNumber(lastyear) + 20;
    } else if (firstyear === bflastyear) {
      lastyearend = toNumber(lastyear) + (toNumber(lastyear) - toNumber(firstyear));
    } else {
      lastyearend = toNumber(lastyear) + (toNumber(lastyear) - toNumber(bflastyear));
    }

    for (let i = 0; i < yearstmp.length; i++) {
      const begin = yearstmp[i];
      const end = yearstmp[i + 1] ?? lastyearend;
      yearsbeginendtmp.push([begin, end]);
    }
  }

  const result = [yearstmp, yearsbeginendtmp];
  yearsCache.set(key, result);
  return result;
}

function isTruthyNihonSokuchi(v) {
  return v === 1 || v === "1" || v === true || v === "true";
}

export function buildYearFlags(meta) {
  const yearKeys = Object.keys(meta)
    .filter((k) => /^year\d+$/.test(k))
    .sort((a, b) => Number(a.slice(4)) - Number(b.slice(4)));

  return yearKeys
    .map((key, idx) => {
      const year = meta[key];
      if (year == null || year === "") return null;

      return {
        year,
        isNihonSokuchi: isTruthyNihonSokuchi(meta[`year${idx + 1}_nihonsokuchi`]),
      };
    })
    .filter(Boolean);
}

export function pickColorIndex(c, shikii1, shikii2, shikii3, shikii4) {
  const v = Number(c);
  if (!Number.isFinite(v)) return "5";

  const ts = [shikii1, shikii2, shikii3, shikii4]
    .map((x) => Number(x))
    .filter(Number.isFinite)
    .sort((a, b) => b - a);

  for (let i = 0; i < ts.length; i++) {
    if (v >= ts[i]) return String(i + 1);
  }
  return String(ts.length + 1);
}

function buildPlacemarkKml({
  meshcode,
  h,
  c,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  lon,
  lat,
  lon2,
  lat2,
  latc,
  lonc,
  hdis,
}) {
  const colorkml1 = pickColorIndex(c, shikii1, shikii2, shikii3, shikii4);

  return `
  <Placemark>
  <name>メッシュ番号：${meshcode}</name>
  <description>${height_name}：${h}</description>
  <styleUrl>#PolyCol${colorkml1}</styleUrl>
  <Polygon>
  <altitudeMode>relativeToGround</altitudeMode>
  <extrude>1</extrude>
  <outerBoundaryIs>
  <LinearRing>
  <coordinates>${lon},${lat},${hdis}
  ${lon2},${lat},${hdis}
  ${lon2},${lat2},${hdis}
  ${lon},${lat2},${hdis}
  ${lon},${lat},${hdis}
  </coordinates>
  </LinearRing>
  </outerBoundaryIs>
  </Polygon>
  <LookAt>
  <latitude>${latc}</latitude>
  <longitude>${lonc}</longitude>
  <altitude>0</altitude>
  <range>1000</range>
  <tilt>0</tilt>
  <heading>0</heading>
  <altitudeMode>relativeToGround</altitudeMode>
  </LookAt>
  </Placemark>
  `;
}

export function mesh2nd2kml3(
  meshcode,
  h,
  c,
  ratio,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  ratio1set
) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));

  const lat = p / 1.5 + q / 12;
  const lon = u + 100 + v * 0.125;
  const lon2 = lon + 0.125;
  const lat2 = lat + 0.08333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

export function mesh3rd2kml3(
  meshcode,
  h,
  c,
  ratio,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  ratio1set
) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat = p / 1.5 + q / 12 + r / 120;
  const lon = u + 100 + v * 0.125 + w * 0.0125;
  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

export function mesh3rd2kml3_nihon(
  meshcode,
  h,
  c,
  ratio,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  ratio1set
) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat_p = p / 1.5 + q / 12 + r / 120;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

export function mesh4th2kml3(
  meshcode,
  h,
  c,
  ratio,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  ratio1set
) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;
  const latc = lat + 0.008333333 / 4;
  const lonc = lon + 0.0125 / 4;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

export function mesh4th2kml3_nihon(
  meshcode,
  h,
  c,
  ratio,
  shikii1,
  shikii2,
  shikii3,
  shikii4,
  height_name,
  ratio1set
) {
  const str = String(meshcode);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat_p = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;

  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;
  const latc = (lat + lat2) / 2;
  const lonc = (lon + lon2) / 2;

  const hdis = toNumber(h) * toNumber(ratio) * toNumber(ratio1set) + 5;

  return buildPlacemarkKml({
    meshcode: str,
    h,
    c,
    shikii1,
    shikii2,
    shikii3,
    shikii4,
    height_name,
    lon,
    lat,
    lon2,
    lat2,
    latc,
    lonc,
    hdis,
  });
}

export function getKmlSpec(meta, meshLen, isNihonSokuchi = false) {
  const specByLen = {
    6: {
      min: toNumber(meta.kokudo_min),
      ratio: meta.kokudo_ratio,
      shikii: [
        meta.kokudo_shikii1,
        meta.kokudo_shikii2,
        meta.kokudo_shikii3,
        meta.kokudo_shikii4 ?? meta.kokudo_shikii3,
      ],
      heightName: `${meta.hanrei_height_name || ""}${meta.hanrei_height_name_2 || ""}`,
      fn: mesh2nd2kml3,
    },
    8: {
      min: toNumber(meta.min),
      ratio: meta.ratio,
      shikii: [meta.shikii1, meta.shikii2, meta.shikii3, meta.shikii4],
      heightName: `${meta.hanrei_height_name || ""}${meta.hanrei_height_name_2 || ""}`,
      fn: isNihonSokuchi ? mesh3rd2kml3_nihon : mesh3rd2kml3,
    },
    9: {
      min: toNumber(meta.min),
      ratio: meta.ratio,
      shikii: [meta.shikii1, meta.shikii2, meta.shikii3, meta.shikii4],
      heightName: `${meta.hanrei_height_name || ""}${meta.hanrei_height_name_2 || ""}`,
      fn: isNihonSokuchi ? mesh4th2kml3_nihon : mesh4th2kml3,
    },
  };

  return specByLen[meshLen] ?? null;
}

export async function getkml2FromRows(rows, meta, ratio1set, year, isNihonSokuchi = false) {
  if (!rows?.[0]?.[1]) return { kml: "", rows, meshLen: 0 };

  const meshLen = String(rows[0][0] ?? "").length;
  const spec = getKmlSpec(meta, meshLen, isNihonSokuchi);
  if (!spec) return { kml: "", rows, meshLen };
  if (!Number.isFinite(spec.min)) return { kml: "", rows, meshLen };

  let tmp = "";
  const cnt = { "1": 0, "2": 0, "3": 0, "4": 0, "5": 0 };
  const samples = [];

  for (let i = 0; i < rows.length; i++) {
    const mesh = rows[i][0];
    const h = rows[i][1];
    const c = rows[i][2];

    const hv = toNumber(h);
    if (Number.isFinite(hv) && hv > spec.min) {
      const idx = pickColorIndex(
        c,
        spec.shikii[0],
        spec.shikii[1],
        spec.shikii[2],
        spec.shikii[3]
      );
      cnt[idx]++;

      if (idx === "3" && samples.length < 5) {
        samples.push({
          c,
          v: Number(c),
          s1: Number(spec.shikii[0]),
          s2: Number(spec.shikii[1]),
          s3: Number(spec.shikii[2]),
          s4: Number(spec.shikii[3]),
        });
      }

      tmp += spec.fn(
        mesh,
        h,
        c,
        spec.ratio,
        spec.shikii[0],
        spec.shikii[1],
        spec.shikii[2],
        spec.shikii[3],
        spec.heightName,
        ratio1set
      );
    }
  }

  console.log("[kml colorIndex counts]", {
    year,
    isNihonSokuchi,
    cnt,
    shikii: spec.shikii,
    samples,
  });

  return { kml: tmp, rows, meshLen };
}

export function makeLegendFolderKml(meta, yearsbeginend, yearsCount) {
  const enableTimeSpan = Number(yearsCount) >= 2;
  let out = `<Folder><name>凡例</name>`;

  out += `
  <ScreenOverlay>
    <name>凡例本体</name>
    <visibility>1</visibility>
    <Icon><href>hanrei/${meta.no}_common.png</href></Icon>
    <overlayXY x="0" y="1" xunits="fraction" yunits="fraction"/>
    <screenXY x="0" y="1" xunits="fraction" yunits="fraction"/>
    <rotationXY x="0" y="0" xunits="fraction" yunits="fraction"/>
    <size x="0" y="0" xunits="fraction" yunits="fraction"/>
  </ScreenOverlay>
  `;

  yearsbeginend.forEach((pair) => {
    const begin = pair?.[0];
    const end = pair?.[1];
    if (begin == null || begin === "") return;

    const timeSpan =
      enableTimeSpan && begin != null && end != null && begin !== "" && end !== ""
        ? `<TimeSpan><begin>${begin}</begin><end>${end}</end></TimeSpan>`
        : "";

    out += `
    <ScreenOverlay>
      <name>${begin}年表示</name>
      <visibility>1</visibility>
      ${timeSpan}
      <Icon><href>hanrei/${meta.no}_${begin}_year.png</href></Icon>
      <overlayXY x="0" y="1" xunits="fraction" yunits="fraction"/>
      <screenXY x="0" y="1" xunits="fraction" yunits="fraction"/>
      <rotationXY x="0" y="0" xunits="fraction" yunits="fraction"/>
      <size x="0" y="0" xunits="fraction" yunits="fraction"/>
    </ScreenOverlay>
    `;
  });

  out += `</Folder>`;
  return out;
}

export function makekml_years(data999kml, yearbeginend, yearsCount) {
  let out = "";
  const enableTimeSpan = Number(yearsCount) >= 2;

  yearbeginend.forEach((pair, i) => {
    const kmlBody = data999kml?.[i] ?? "";
    if (!kmlBody) return;

    const begin = pair?.[0];
    const end = pair?.[1];
    if (begin == null || begin === "") return;

    const timeSpan =
      enableTimeSpan && begin != null && end != null && begin !== "" && end !== ""
        ? `<TimeSpan><begin>${begin}</begin><end>${end}</end></TimeSpan>`
        : "";

    out += `<Folder><name>${String(begin)}</name>${timeSpan}${kmlBody}</Folder>\n`;
  });

  return out;
}

function toKmlAlpha(alpha) {
  const a = Number(alpha);
  if (!Number.isFinite(a)) return "c8";
  if (a <= 0.01) return "00";
  if (a <= 0.2) return "33";
  if (a <= 0.4) return "66";
  if (a <= 0.9) return "c8";
  return "ff";
}

function toKmlColorSafe(cssHex, alpha) {
  const s = String(cssHex ?? "").trim();
  if (!/^#[0-9a-fA-F]{6}$/.test(s)) return null;

  const rr = s.slice(1, 3);
  const gg = s.slice(3, 5);
  const bb = s.slice(5, 7);
  const aa = toKmlAlpha(alpha);

  return `${aa}${bb}${gg}${rr}`;
}

export async function makekml(kml_body, viewarray, meta, area, alpha) {
  console.log("[kml colors raw]", {
    kml1: meta.kml1_color,
    kml2: meta.kml2_color,
    kml3: meta.kml3_color,
    kml4: meta.kml4_color,
    kml5: meta.kml5_color,
    no: meta.no,
  });

  const kml1_color = toKmlColorSafe(meta.kml1_color, alpha);
  const kml2_color = toKmlColorSafe(meta.kml2_color, alpha);
  const kml3_color = toKmlColorSafe(meta.kml3_color, alpha);
  const kml4_color = toKmlColorSafe(meta.kml4_color, alpha);
  const kml5_color = toKmlColorSafe(meta.kml5_color, alpha);

  const title = meta.title;
  const cityname = area;
  const [latcenter, loncenter, lonheightratio] = viewarray;

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${cityname + "_" + title}</name>

    <Style id="PolyCol1"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml1_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol2"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml2_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol3"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml3_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol4"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml4_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>
    <Style id="PolyCol5"><LineStyle><color>C8FFFFFF</color><width>1</width></LineStyle><PolyStyle><color>${kml5_color || "C8FFFFFF"}</color><fill>1</fill><outline>1</outline></PolyStyle></Style>

    <LookAt>
      <longitude>${loncenter}</longitude>
      <latitude>${latcenter}</latitude>
      <altitude>0</altitude>
      <range>${lonheightratio}</range>
      <tilt>30</tilt>
      <heading>-25</heading>
      <altitudeMode>absolute</altitudeMode>
    </LookAt>

    ${kml_body}

  </Document>
</kml>`;
}

export function mesh2ndcenter(meshcode2nd) {
  const str = String(meshcode2nd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));

  const lat = p / 1.5 + q / 12;
  const lon = u + 100 + v * 0.125;
  const lon2 = lon + 0.125;
  const lat2 = lat + 0.08333333;

  return [(lat + lat2) / 2, (lon + lon2) / 2];
}

export function mesh3rdcenter(meshcode3rd) {
  const str = String(meshcode3rd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat = p / 1.5 + q / 12 + r / 120;
  const lon = u + 100 + v * 0.125 + w * 0.0125;
  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;

  return [(lat + lat2) / 2, (lon + lon2) / 2];
}

export function mesh3rdcenter_nihon(meshcode3rd) {
  const str = String(meshcode3rd);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));

  const lat_p = p / 1.5 + q / 12 + r / 120;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;
  const lon2 = lon + 0.0125;
  const lat2 = lat + 0.008333333;

  return [(lat + lat2) / 2, (lon + lon2) / 2];
}

export function mesh4thcenter(meshcode4th) {
  const str = String(meshcode4th);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;
  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;

  return [(lat + lat2) / 2, (lon + lon2) / 2];
}

export function mesh4thcenter_nihon(meshcode4th) {
  const str = String(meshcode4th);
  const p = parseInt(str.slice(0, 2));
  const u = parseInt(str.slice(2, 4));
  const q = parseInt(str.slice(4, 5));
  const v = parseInt(str.slice(5, 6));
  const r = parseInt(str.slice(6, 7));
  const w = parseInt(str.slice(7, 8));
  const m = parseInt(str.slice(8, 9));

  const lat_p = p / 1.5 + q / 12 + r / 120 + ((m > 2 ? 1 : 0) * 15) / 3600;
  const lon_p = u + 100 + v * 0.125 + w * 0.0125 + ((m % 2 === 0 ? 1 : 0) * 22.5) / 3600;

  const lat = lat_p - 0.00010695 * lat_p + 0.000017464 * lon_p + 0.0046017;
  const lon = lon_p - 0.000046038 * lat_p - 0.000083043 * lon_p + 0.01004;
  const lon2 = lon + 0.0125 / 2;
  const lat2 = lat + 0.008333333 / 2;

  return [(lat + lat2) / 2, (lon + lon2) / 2];
}

export function calcCenterFromRows(rows, meshLen, minValue, isNihonSokuchi = false) {
  if (!rows || rows.length === 0) return [0, 0, 100000];

  let pickCenterFn;
  if (meshLen === 6) {
    pickCenterFn = mesh2ndcenter;
  } else if (meshLen === 9) {
    pickCenterFn = isNihonSokuchi ? mesh4thcenter_nihon : mesh4thcenter;
  } else {
    pickCenterFn = isNihonSokuchi ? mesh3rdcenter_nihon : mesh3rdcenter;
  }

  let latmax = -Infinity;
  let latmin = Infinity;
  let lonmax = -Infinity;
  let lonmin = Infinity;

  for (let i = 0; i < rows.length; i++) {
    if (toNumber(rows[i][1]) > toNumber(minValue)) {
      const [lat, lon] = pickCenterFn(rows[i][0]);

      if (latmax < lat) latmax = lat;
      if (lonmax < lon) lonmax = lon;
      if (latmin > lat) latmin = lat;
      if (lonmin > lon) lonmin = lon;
    }
  }

  if (!isFinite(latmax) || !isFinite(latmin) || !isFinite(lonmax) || !isFinite(lonmin)) {
    return [0, 0, 100000];
  }

  const latcenter = (latmax + latmin) / 2;
  const loncenter = (lonmax + lonmin) / 2;

  let lonheightratio = 170000 * (lonmax - lonmin);
  if (lonmax <= lonmin) lonheightratio = 100000;

  return [latcenter, loncenter, lonheightratio];
}

export function buildNetworkLinkUrl(baseUrl, params) {
  const u = new URL(baseUrl);
  Object.entries(params).forEach(([k, v]) => {
    if (v != null && v !== "") {
      u.searchParams.set(k, String(v));
    }
  });
  return u.toString();
}

export function makeNetworkLinkKml(networkUrl, name = "network link", extraKml = "") {
  const escapeXml = (str) =>
    String(str ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&apos;");

  return `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>${escapeXml(name)}</name>

    ${extraKml || ""}

    <NetworkLink>
      <name>${escapeXml(name)}</name>
      <open>1</open>
      <Link>
        <href>${escapeXml(networkUrl)}</href>
        <refreshMode>onChange</refreshMode>
      </Link>
    </NetworkLink>
  </Document>
</kml>`;
}